import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@patient-watch/ui/sonner";
import { useAuthCopy } from "@/features/i18n/authCopy";

import type { PublicSmartAuthResult } from "../api/invokePublicSmartAuth";
import { invokePublicSmartAuth } from "../api/invokePublicSmartAuth";
import { requestLoginPhoneSmsOtp } from "../api/passwordlessChannelRequests";
import { buildLoginRouteSearch } from "@/features/auth/search/authRouteSearch";
import { contactMethodToMethodParam } from "@/features/auth/utils/authMethodSearchParam";
import { smsOtpLoginAfterLookupMessage } from "../utils/smsOtpUserFacingMessage";
import type { SmartAuthSchemaType } from "../types/smartAuthSchema";
import { normalizeAuthPhone } from "../utils/normalizeAuthPhone";

type SmartAuthMutationData = { smart: PublicSmartAuthResult };

const useSmartAuthMutation = () => {
    const navigate = useNavigate();
    const copy = useAuthCopy();
    return useMutation({
        mutationKey: ["login"],
        mutationFn: async (formData: SmartAuthSchemaType): Promise<SmartAuthMutationData> => {
            const email = formData.contactMethod === "email" ? formData.email?.trim() || undefined : undefined;
            const phoneRaw = formData.contactMethod === "phone" ? formData.phone?.trim() || undefined : undefined;
            const phone = phoneRaw ? (normalizeAuthPhone(phoneRaw) ?? undefined) : undefined;

            const smart = await invokePublicSmartAuth({
                email,
                phone,
            });

            return { smart };
        },
        onError: (error) => {
            console.error(error);
            toast.error(error.message);
        },
        onSuccess: async (payload, variables: SmartAuthSchemaType) => {
            const data = payload.smart;

            const search: {
                email?: string;
                phone?: string;
                invited_by_name?: string | null;
                contactMethod?: "email" | "phone";
                method?: "email" | "phone";
            } = { invited_by_name: data.invited_by_name };
            if (data.email) search.email = data.email;
            if (data.phone) search.phone = data.phone;
            search.contactMethod = variables.contactMethod;
            search.method = contactMethodToMethodParam(variables.contactMethod);

            if (data.auth_method === "login") {
                if (data.phone_otp_ready === false) {
                    if (data.email) {
                        void navigate({
                            to: "/login",
                            search: buildLoginRouteSearch({
                                email: data.email,
                                invited_by_name: data.invited_by_name,
                                contactMethod: "email",
                            }),
                        });
                        toast.info(copy.accountFoundUseEmailSmsNotReady);
                    } else {
                        toast.error(copy.smsLoginContactCareTeam);
                        void navigate({
                            to: "/login",
                            search: buildLoginRouteSearch({
                                phone: data.phone,
                                invited_by_name: data.invited_by_name,
                                contactMethod: variables.contactMethod,
                            }),
                        });
                    }
                    return;
                }

                if (variables.contactMethod === "phone") {
                    const phoneForOtp = normalizeAuthPhone((data.phone ?? variables.phone ?? "").trim());
                    if (phoneForOtp) {
                        try {
                            await requestLoginPhoneSmsOtp(phoneForOtp);
                            toast.success(copy.codeSentToPhone);
                            void navigate({
                                to: "/login/verify",
                                search: { phone: phoneForOtp },
                            });
                        } catch (e) {
                            const err = e instanceof Error ? e : null;
                            toast.error(
                                smsOtpLoginAfterLookupMessage(
                                    err?.message ?? copy.somethingWentWrong,
                                    err ? (err as { code?: string }).code : undefined
                                )
                            );
                            void navigate({
                                to: "/login",
                                search: buildLoginRouteSearch({
                                    email: search.email,
                                    phone: phoneForOtp,
                                    invited_by_name: search.invited_by_name,
                                    contactMethod: "phone",
                                }),
                            });
                        }
                        return;
                    }
                }

                toast.info(copy.accountFound);
                void navigate({
                    to: "/login",
                    search: buildLoginRouteSearch({
                        email: search.email,
                        phone: search.phone,
                        invited_by_name: search.invited_by_name,
                        contactMethod: search.contactMethod ?? "email",
                    }),
                });
            } else if (data.auth_method === "confirm-account") {
                toast.info(copy.invitationFound);
                void navigate({
                    to: "/confirm-account",
                    search,
                });
            } else if (data.auth_method === "signup") {
                toast.info(copy.newUser);
                void navigate({ to: "/new-user", search });
            } else {
                console.error(data);
                toast.error(copy.authMethodError);
            }
        },
    });
};

export default useSmartAuthMutation;
